(() => {
  if (customElements.get('arista-inline-watch-shop')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AristaInlineWatchShop extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.items = [...this.querySelectorAll('[data-arista-inline-watch-item]')]
        .map((element) => ({
          element,
          video: element.querySelector('[data-arista-inline-watch-video]'),
          source: element.querySelector('[data-arista-inline-watch-video] source[data-src]'),
          button: element.querySelector('[data-arista-inline-watch-toggle]'),
          label: element.querySelector('[data-arista-inline-watch-label]'),
          status: element.querySelector('[data-arista-inline-watch-status]'),
          title: element.querySelector('.arista-watch-rail__card-title')?.textContent.trim() || 'Arista video',
          userPaused: false,
          manualPlay: false,
        }))
        .filter((item) => item.video && item.button);

      if (!this.items.length) return;

      this.initialized = true;
      this.visibility = new Map();
      this.saveData = Boolean(navigator.connection?.saveData);
      this.handleVisibility = () => {
        if (document.hidden) this.pauseAll();
      };
      this.handleMotionChange = () => this.resetObserver();

      this.items.forEach((item) => {
        item.video.muted = true;
        item.handleToggle = () => this.toggle(item);
        item.handlePlay = () => {
          this.pauseOthers(item.video);
          this.update(item, false);
        };
        item.handlePause = () => this.update(item, item.userPaused);
        item.button.addEventListener('click', item.handleToggle);
        item.video.addEventListener('play', item.handlePlay);
        item.video.addEventListener('pause', item.handlePause);
        this.update(item, false);
      });

      document.addEventListener('visibilitychange', this.handleVisibility);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', this.handleMotionChange);
      else reducedMotion.addListener(this.handleMotionChange);
      this.resetObserver();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.observer?.disconnect();
      this.items.forEach((item) => {
        item.video.pause();
        item.button.removeEventListener('click', item.handleToggle);
        item.video.removeEventListener('play', item.handlePlay);
        item.video.removeEventListener('pause', item.handlePause);
      });
      document.removeEventListener('visibilitychange', this.handleVisibility);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', this.handleMotionChange);
      else reducedMotion.removeListener(this.handleMotionChange);
      this.initialized = false;
    }

    resetObserver() {
      this.observer?.disconnect();
      this.visibility.clear();
      this.pauseAll();

      if (reducedMotion.matches || this.saveData || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const item = this.items.find((candidate) => candidate.element === entry.target);
            if (item) this.visibility.set(item, entry.isIntersecting ? entry.intersectionRatio : 0);
          });

          const manualActive = this.items.find(
            (item) => item.manualPlay && !item.video.paused && (this.visibility.get(item) || 0) >= 0.05,
          );

          const active = manualActive || this.items
            .filter((item) => !item.userPaused && (this.visibility.get(item) || 0) >= 0.62)
            .sort((first, second) => (this.visibility.get(second) || 0) - (this.visibility.get(first) || 0))[0];

          this.items.forEach((item) => {
            if (item === active) {
              if (item.video.paused) this.play(item, false);
            } else if (!item.video.paused) {
              item.manualPlay = false;
              item.video.pause();
            }
          });
        },
        { threshold: [0, 0.01, 0.62, 1] },
      );

      this.items.forEach((item) => this.observer.observe(item.element));
    }

    toggle(item) {
      if (item.video.paused) {
        item.userPaused = false;
        item.manualPlay = true;
        this.play(item, true);
      } else {
        item.userPaused = true;
        item.manualPlay = false;
        item.video.pause();
      }
    }

    play(item, announce) {
      this.ensureSource(item);
      this.pauseOthers(item.video);
      const promise = item.video.play();
      if (!promise) return;
      promise
        .then(() => this.update(item, announce))
        .catch(() => {
          item.manualPlay = false;
          item.video.pause();
          try {
            item.video.currentTime = 0;
            item.video.load();
          } catch (_error) {
            // The poster and Play label remain as the safe fallback.
          }
          this.update(item, false, `${item.title} is ready to play. Tap Play video to try again.`);
        });
    }

    ensureSource(item) {
      if (!item.source?.dataset.src || item.source.src) return;
      item.source.src = item.source.dataset.src;
      item.source.removeAttribute('data-src');
      item.video.load();
    }

    pauseOthers(activeVideo) {
      this.items.forEach((item) => {
        if (item.video !== activeVideo) {
          item.manualPlay = false;
          if (!item.video.paused) item.video.pause();
        }
      });
    }

    pauseAll() {
      this.items.forEach((item) => {
        item.manualPlay = false;
        item.video.pause();
      });
    }

    update(item, announce, message) {
      const playing = !item.video.paused && !item.video.ended;
      if (item.label) item.label.textContent = playing ? 'Pause video' : 'Play video';
      item.button.dataset.playing = String(playing);
      item.button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${item.title}`);
      if (item.status) {
        const statusText = message || `${item.title} is ${playing ? 'playing' : 'paused'}.`;
        item.status.setAttribute('aria-live', announce || message ? 'polite' : 'off');

        if (announce || message) {
          item.status.textContent = '';
          window.requestAnimationFrame(() => {
            item.status.textContent = statusText;
          });
        } else {
          item.status.textContent = statusText;
        }
      }
    }
  }

  customElements.define('arista-inline-watch-shop', AristaInlineWatchShop);
})();
